type TenantLike = {
  id?: number | string
  code?: string
}

const TENANT_ROUTE_PREFIX = '/t'
const ADMIN_ROUTE_PREFIX = '/admin'
const ADMIN_TENANT_ROUTE_PREFIX = '/admin/t'
const PROJECT_ROUTE_PREFIX = '/projects'
const TENANT_BUSINESS_ROUTE_PREFIXES = [
  '/goals',
  '/products',
  '/portfolio',
  PROJECT_ROUTE_PREFIX,
  '/requirements',
  '/incidents',
  '/virtual-employee',
  '/feedback',
  '/efficiency',
  '/collect',
  '/memos',
  '/profile',
]
const TENANT_ADMIN_ROUTE_PREFIXES = [
  '/admin/tenant',
  '/admin/department',
  '/admin/user',
  '/admin/audits',
  '/admin/roles',
  '/admin/permissions',
  '/admin/dict',
  '/admin/positions',
  '/admin/department-positions',
  '/admin/squads',
  '/admin/tracks',
  '/admin/im',
  '/admin/org-integration',
]

export function getCurrentTenantCode(): string {
  try {
    const raw = localStorage.getItem('currentTenant')
    const tenant = raw ? JSON.parse(raw) as TenantLike : null
    return normalizeTenantCode(tenant?.code)
  } catch {
    return ''
  }
}

export function toTenantPath(path: string, tenantCode = getCurrentTenantCode()): string {
  if (!path || path.startsWith(`${TENANT_ROUTE_PREFIX}/`)) return path
  if (!isTenantBusinessPath(path)) return path

  const normalizedTenantCode = normalizeTenantCode(tenantCode)
  if (!normalizedTenantCode) return path

  return `${TENANT_ROUTE_PREFIX}/${encodeURIComponent(normalizedTenantCode)}${path}`
}

export function toTenantAdminPath(path: string, tenantCode = getCurrentTenantCode()): string {
  if (!path || path.startsWith(`${ADMIN_TENANT_ROUTE_PREFIX}/`)) return path
  if (!isTenantAdminPath(path)) return path

  const normalizedTenantCode = normalizeTenantCode(tenantCode)
  if (!normalizedTenantCode) return path

  const adminSuffix = path.slice(ADMIN_ROUTE_PREFIX.length)
  return `${ADMIN_TENANT_ROUTE_PREFIX}/${encodeURIComponent(normalizedTenantCode)}${adminSuffix}`
}

export function toTenantBusinessPath(modulePath: string, suffix = '', tenantCode?: string): string {
  const normalizedModulePath = modulePath.startsWith('/') ? modulePath : `/${modulePath}`
  const normalizedSuffix = suffix
    ? `/${String(suffix).replace(/^\/+/, '')}`
    : ''
  return toTenantPath(`${normalizedModulePath}${normalizedSuffix}`, tenantCode)
}

export function toTenantProjectPath(suffix = '', tenantCode?: string): string {
  return toTenantBusinessPath(PROJECT_ROUTE_PREFIX, suffix, tenantCode)
}

export function toTenantProjectDetailPath(projectId: number | string, tenantCode?: string): string {
  return toTenantProjectPath(`/${projectId}`, tenantCode)
}

export function toTenantRequirementPath(suffix = '', tenantCode?: string): string {
  return toTenantBusinessPath('/requirements', suffix, tenantCode)
}

export function toTenantRequirementDetailPath(requirementId: number | string, tenantCode?: string): string {
  return toTenantRequirementPath(`/${requirementId}`, tenantCode)
}

export function stripTenantPath(path: string): string {
  return path
    .replace(/^\/admin\/t\/[^/]+(?=\/)/, ADMIN_ROUTE_PREFIX)
    .replace(/^\/t\/[^/]+(?=\/)/, '')
}

export function replaceTenantCodeInPath(path: string, tenantCode: string): string {
  const normalizedTenantCode = normalizeTenantCode(tenantCode)
  if (!path || !normalizedTenantCode) return path
  if (path.startsWith(`${ADMIN_TENANT_ROUTE_PREFIX}/`)) {
    return path.replace(
      /^\/admin\/t\/[^/]+(?=\/)/,
      `${ADMIN_TENANT_ROUTE_PREFIX}/${encodeURIComponent(normalizedTenantCode)}`
    )
  }
  if (isTenantAdminPath(path)) {
    return toTenantAdminPath(path, normalizedTenantCode)
  }
  if (path.startsWith(`${TENANT_ROUTE_PREFIX}/`)) {
    return path.replace(/^\/t\/[^/]+(?=\/)/, `${TENANT_ROUTE_PREFIX}/${encodeURIComponent(normalizedTenantCode)}`)
  }
  return toTenantPath(path, normalizedTenantCode)
}

export function routeParamToString(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] || '')
  return String(value || '')
}

export function isTenantBusinessPath(path: string): boolean {
  return TENANT_BUSINESS_ROUTE_PREFIXES.some(prefix => isPathWithPrefix(path, prefix))
}

export function isTenantAdminPath(path: string): boolean {
  return TENANT_ADMIN_ROUTE_PREFIXES.some(prefix => isPathWithPrefix(path, prefix))
}

export function normalizeTenantCode(value?: string): string {
  return String(value || '').trim()
}

function isPathWithPrefix(path: string, prefix: string): boolean {
  return path === prefix
    || path.startsWith(`${prefix}/`)
    || path.startsWith(`${prefix}?`)
    || path.startsWith(`${prefix}#`)
}
